import { createAsyncThunk } from "@reduxjs/toolkit";
import client, {
  ApiError,
  ApiResponse,
  AsyncThunkConfig,
  isAxiosError,
} from "../../../api/axiosConfig";
import { PsychologicalLab } from "../../../api/psychologicalLabApi";
import {
  psychologicalLabsURL,
  psychologicalLabsGrantedURL,
} from "../../../utils/paths";
import {
  dispatchNotification,
  getTranslatedBackendMessage,
} from "../../../utils/redux";
import {
  CreatePsychLabData,
  EditPsychologicalLabDataRequest,
} from "../../../components/Forms/types";
import { I18n } from "react-redux-i18n";

export const fetchPsychologicalLabs = createAsyncThunk<
  PsychologicalLab[],
  { includeInactive?: boolean } | undefined,
  AsyncThunkConfig
>("psychologicalLabs/fetchPsychologicalLabs", async (arg, thunkAPI) => {
  try {
    const params: Record<string, string> = { search: "" };

    if (arg?.includeInactive) {
      const inactiveQueryKey = "include_inactive";

      params[inactiveQueryKey] = "true";
    }

    const response = await client.get<ApiResponse<PsychologicalLab[]>>(
      `${psychologicalLabsGrantedURL}`,
      { params }
    );

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message, true);

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

export const fetchSerchedPsychologicalLabs = createAsyncThunk<
  PsychologicalLab[],
  string,
  AsyncThunkConfig
>(
  "psychologicalLabs/fetchSerchedPsychologicalLabs",
  async (searchTerm, thunkAPI) => {
    try {
      const inactiveQueryKey = "include_inactive";
      const response = await client.get<ApiResponse<PsychologicalLab[]>>(
        `${psychologicalLabsGrantedURL}`,
        { params: { search: searchTerm, [inactiveQueryKey]: true } }
      );

      return response.data.results;
    } catch (err) {
      if (isAxiosError(err) && err.response != null) {
        const extractedError = err.response.data as ApiError;

        dispatchNotification("error", extractedError.message, true);

        return thunkAPI.rejectWithValue(extractedError);
      }

      dispatchNotification("error");
      throw err;
    }
  }
);

export const createPsychLab = createAsyncThunk<
  PsychologicalLab,
  CreatePsychLabData,
  AsyncThunkConfig
>("psychologicalLabsCRUD/createPsychLab", async (data, thunkAPI) => {
  try {
    const response = await client.post<ApiResponse<PsychologicalLab>>(
      psychologicalLabsURL,
      data
    );

    dispatchNotification("success", I18n.t("Snackbars.createPsychLabSuccess"));

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message, true);

      return thunkAPI.rejectWithValue(extractedError);
    }
    dispatchNotification("error");

    return thunkAPI.rejectWithValue({
      results: null,
      error: true,
      message: "Unknown error",
      details: undefined,
    });
  }
});

export const editPsychLab = createAsyncThunk<
  PsychologicalLab,
  EditPsychologicalLabDataRequest,
  AsyncThunkConfig
>("psychologicalLabsCRUD/editPsychLab", async (data, thunkAPI) => {
  try {
    const response = await client.patch<ApiResponse<PsychologicalLab>>(
      `${psychologicalLabsURL}${data.id}/`,
      { ...data }
    );

    dispatchNotification("success", I18n.t("Snackbars.editPsychLabSuccess"));

    return response.data.results;
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification("error", extractedError.message, true);

      return thunkAPI.rejectWithValue(extractedError);
    }
    dispatchNotification("error");
    throw err;
  }
});

export const anonymizePsychLab = createAsyncThunk<
  { count: number; message: string },
  number,
  AsyncThunkConfig
>("psychologicalLabsCRUD/anonymizePsychLab", async (labId, thunkAPI) => {
  try {
    const response = await client.post<
      | { results: { message: string; count?: number } }
      | { message: string; count?: number }
    >(`${psychologicalLabsURL}${labId}/anonymize/`);

    const responseData = response.data;
    const data =
      responseData &&
      ("results" in responseData ? responseData.results : responseData);
    const backendMessage = data?.message;
    const count = data?.count;
    const isEmpty = count === 0;
    const message = isEmpty
      ? I18n.t("Snackbars.anonymizePsychLabEmptySuccess")
      : backendMessage || I18n.t("Snackbars.anonymizePsychLabSuccess");

    dispatchNotification("success", message);

    return { count: 0, message };
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification(
        "error",
        getTranslatedBackendMessage(extractedError.message)
      );

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});

export const deletePsychLab = createAsyncThunk<
  undefined,
  number,
  AsyncThunkConfig
>("psychologicalLabsCRUD/deletePsychLab", async (arg, thunkAPI) => {
  try {
    await client.delete(`${psychologicalLabsURL}${arg}/`);

    dispatchNotification("success", I18n.t("Snackbars.deletePsychLabSuccess"));
  } catch (err) {
    if (isAxiosError(err) && err.response != null) {
      const extractedError = err.response.data as ApiError;

      dispatchNotification(
        "error",
        getTranslatedBackendMessage(extractedError.message)
      );

      return thunkAPI.rejectWithValue(extractedError);
    }

    dispatchNotification("error");
    throw err;
  }
});